/**
 * Raw Hot Spots layer (Phase E.W.0.5) — PRE-FUSION satellite thermal anomalies
 * from the E.1 `hotspot_detections` table (FIRMS NRT + CWFIS M3 + GOES-R FDCC),
 * the lower-confidence sibling of the F.1-scored High-Risk Hotspots.
 *
 * Per operator-cop-doctrine §3B.8 raw hotspots live in the AMBIENT register: a
 * SMALL fixed dot (7px — smaller than the L1 anomaly dot's 10px), NEVER pulsing,
 * coloured by SOURCE (styles/rawHotspots) so an operator reads which satellite saw
 * the heat. They must never compete with the vivid Response-Level Anomaly-Queue
 * dots that the fusion engine produces.
 *
 * Render gate (build.md § L.2 + §9.1): HIDDEN at country zoom, visible regional+.
 * Each dot carries a distanceDisplayCondition with far = REGIONAL_VISIBLE_FAR_M —
 * the same eye-distance threshold the Live Video Feeds snapshot tier-in uses
 * (coneVisibility.ts) — so the national Default view stays free of raw thermal
 * clutter while a regional operator sees the full detection field.
 *
 * Imperative installer (MapView owns the viewer): requestRefresh on camera moves,
 * setVisible on the toggle, pick from the central click handler → RawHotspotFeature
 * → the RawHotspotBody side panel.
 */
import * as Cesium from 'cesium';
import type { RawHotspotFeature, RawHotspotFeatureCollection } from '@tsr/shared-types';
import { fetchRawHotspots, type BboxQuery, type RawHotspotFetchOpts } from '../api';
import { rawHotspotStyle, RAW_HOTSPOT_DOT_SIZE, RAW_HOTSPOT_DOT_ALPHA } from '../styles/rawHotspots';
import { REGIONAL_VISIBLE_FAR_M } from '../coneVisibility';

const REFRESH_DEBOUNCE_MS = 300;
const RAW_PREFIX = 'rawhotspot:';

export interface RawHotspotsLayerOptions {
  onCount?: (n: number) => void;
  getVisible?: () => boolean;
  /** Test seam: inject a fetcher in place of the real API call. */
  fetcher?: (
    bbox: BboxQuery,
    signal: AbortSignal,
    opts?: RawHotspotFetchOpts,
  ) => Promise<RawHotspotFeatureCollection>;
}

export interface RawHotspotsLayerHandle {
  requestRefresh(bbox: BboxQuery): void;
  refreshNow(bbox: BboxQuery): Promise<void>;
  setVisible(visible: boolean): void;
  /** Map a picked Cesium entity back to its raw hotspot feature (null if not ours). */
  pick(entity: unknown): RawHotspotFeature | null;
  destroy(): void;
}

export function installRawHotspotsLayer(
  viewer: Cesium.Viewer,
  opts: RawHotspotsLayerOptions = {},
): RawHotspotsLayerHandle {
  const fetcher = opts.fetcher ?? fetchRawHotspots;
  const ds = new Cesium.CustomDataSource('raw-hotspots');
  void viewer.dataSources.add(ds);

  // Hide at country zoom, show regional+ (the L.2 render gate). Shared by every
  // dot so the whole field tiers in together as the eye descends.
  const gate = new Cesium.DistanceDisplayCondition(0, REGIONAL_VISIBLE_FAR_M);

  const entityToFeature = new Map<Cesium.Entity, RawHotspotFeature>();
  let inflight: AbortController | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;
  // Guards against an older in-flight load resolving after a newer one.
  let loadGen = 0;

  const render = (fc: RawHotspotFeatureCollection): void => {
    ds.entities.removeAll();
    entityToFeature.clear();
    for (const feature of fc.features) {
      const style = rawHotspotStyle(feature.properties.source);
      const [lon, lat] = feature.geometry.coordinates;
      const dot = ds.entities.add({
        id: `${RAW_PREFIX}${feature.id}`,
        position: Cesium.Cartesian3.fromDegrees(lon, lat),
        point: {
          // Ambient: a small, fixed dot — raw thermal NEVER pulses (§3B.8 motion
          // budget reserves motion for the scored Anomaly-Queue dots).
          pixelSize: RAW_HOTSPOT_DOT_SIZE,
          color: Cesium.Color.fromCssColorString(style.color).withAlpha(RAW_HOTSPOT_DOT_ALPHA),
          outlineColor: Cesium.Color.fromCssColorString('#0b1020'),
          outlineWidth: 1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          // L.2 render gate: hidden at country zoom, visible regional+.
          distanceDisplayCondition: gate,
        },
      });
      entityToFeature.set(dot, feature);
    }
  };

  const refreshNow = async (bbox: BboxQuery): Promise<void> => {
    if (destroyed) return;
    if (inflight) inflight.abort();
    const ctl = new AbortController();
    inflight = ctl;
    const gen = ++loadGen;
    try {
      const fc = await fetcher(bbox, ctl.signal);
      if (destroyed || ctl.signal.aborted || gen !== loadGen) return;
      render(fc);
      opts.onCount?.(fc.features.length);
      ds.show = opts.getVisible ? opts.getVisible() : true;
    } catch (err) {
      if ((err as { name?: string }).name === 'AbortError') return;
      console.error('fetchRawHotspots failed', err);
    }
  };

  return {
    requestRefresh(bbox: BboxQuery): void {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => void refreshNow(bbox), REFRESH_DEBOUNCE_MS);
    },
    refreshNow,
    setVisible(visible: boolean): void {
      ds.show = visible;
    },
    pick(entity: unknown): RawHotspotFeature | null {
      if (!entity || typeof entity !== 'object') return null;
      return entityToFeature.get(entity as Cesium.Entity) ?? null;
    },
    destroy(): void {
      destroyed = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (inflight) inflight.abort();
      entityToFeature.clear();
      if (!viewer.isDestroyed()) viewer.dataSources.remove(ds, true);
    },
  };
}
